/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9: today's workouts on My training, each as the Programming card's
 * athlete view, grouped by program, with the Programming results dialogs.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@lemonadetraining/web-ui";
import { IconCheck } from "@tabler/icons-react";
import type { OneRepMax } from "@lemonadetraining/contracts";
import WorkoutPartView from "../program/workout/WorkoutPartView";
import ViewAthletesScoresWidget from "../Workout/ViewAthletesScoresWidget";
import WorkoutScore from "../Workout/WorkoutScore";
import type { TodayProgram, TodayWorkout } from "../../lib/todayWorkouts";
import type { TodaysWorkoutStatus } from "../../hooks/useTodaysWorkout";

export interface TodayWorkoutsProps {
  status: TodaysWorkoutStatus;
  programs: TodayProgram[];
  partial: boolean;
  onRetry: () => void;
  /** The athlete's tested maxes for their own weight on % loads; undefined until known. */
  oneRepMaxes: OneRepMax[] | undefined;
  /** "Mon 28 Sep" in the gym's calendar. */
  dateLabel: string;
  /** The movement library behind the member's own weights. */
  weights: "loading" | "ready" | "error";
  /** One line under the heading, e.g. which clock "today" is on. */
  note?: React.ReactNode;
}

type ResultsView = { workout: TodayWorkout; mode: "see" | "record" } | null;

const hasPercentLoad = (w: TodayWorkout) =>
  (w.workoutData.movements ?? []).some((m) => typeof m.percent === "number" && m.percent > 0);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const titleOf = (w: TodayWorkout) => w.workoutData.title?.trim() || "Today's workout";

export default function TodayWorkouts({
  status,
  programs,
  partial,
  onRetry,
  oneRepMaxes,
  dateLabel,
  weights,
  note,
}: TodayWorkoutsProps) {
  const navigate = useNavigate();
  const [results, setResults] = useState<ResultsView>(null);
  // Results recorded on this visit, on top of what the server reported.
  const [recorded, setRecorded] = useState<ReadonlySet<string>>(new Set());
  const needsWeights = programs.some((p) => p.workouts.some(hasPercentLoad));
  const count = programs.reduce((n, p) => n + p.workouts.length, 0);

  return (
    <section className="flex min-w-0 flex-col gap-3" aria-labelledby="today-workouts-heading">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="today-workouts-heading" className="text-base font-semibold">
          Today
        </h2>
        <span className="text-sm text-muted-foreground" data-testid="today-workouts-summary">
          {dateLabel}
          {status === "ready" &&
            ` · ${plural(count, "workout")}${programs.length > 1 ? ` from ${plural(programs.length, "program")}` : ""}`}
        </span>
      </div>
      {note}

      {status === "loading" || status === "idle" || (needsWeights && weights === "loading") ? (
        <div className="flex flex-col gap-3" data-testid="today-workouts-loading">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : status === "clock-error" ? (
        <div
          className="rounded-xl border p-4"
          role="alert"
          data-testid="today-workouts-clock-error"
        >
          <p className="text-sm">
            Your device&apos;s clock looks wrong, so we can&apos;t tell which day it is. Check its
            date and time, then reload.
          </p>
        </div>
      ) : status === "error" ? (
        <div
          className="flex flex-col items-start gap-2 rounded-xl border p-4"
          role="alert"
          data-testid="today-workouts-error"
        >
          <p className="text-sm">
            We couldn&apos;t load today&apos;s workouts. Check your connection and try again.
          </p>
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : status === "empty" ? (
        <div className="rounded-xl border p-4" data-testid="today-workouts-empty">
          <p className="text-sm text-muted-foreground">
            {partial
              ? "Some of today's workouts didn't load."
              : "No workout for today yet. When your gym or coach posts one, it shows here."}
          </p>
          {partial && (
            <Button variant="outline" className="mt-2" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      ) : (
        <>
          {partial && (
            <p
              className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground"
              role="status"
            >
              Some of today&apos;s workouts didn&apos;t load.
              <Button variant="link" className="h-auto min-h-11 p-0 text-sm" onClick={onRetry}>
                Try again
              </Button>
            </p>
          )}
          {needsWeights && weights === "error" && (
            <p role="alert" className="text-sm text-destructive" data-testid="today-weights-error">
              Couldn&apos;t load your personalised weights. Refresh the page to try again.
            </p>
          )}
          {programs.map((program) => (
            <div
              key={program.id}
              className="flex flex-col gap-2"
              data-testid={`today-program-${program.id}`}
            >
              <h3 id={`today-program-heading-${program.id}`} className="text-sm font-medium">
                {program.name}
                {program.source && (
                  <span className="font-normal text-muted-foreground"> · {program.source}</span>
                )}
              </h3>
              <ul
                className="flex w-full flex-col gap-3"
                aria-labelledby={`today-program-heading-${program.id}`}
              >
                {program.workouts.map((workout) => (
                  <li
                    key={workout.id}
                    className="flex flex-col gap-3 rounded-xl border bg-card p-4"
                    data-testid={`today-workout-${workout.id}`}
                  >
                    <WorkoutPartView
                      workout={workout}
                      headingLevel={4}
                      oneRepMaxes={oneRepMaxes}
                      onLogMovement={(id) => navigate(`/dashboard/movements/${id}?log=1`)}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      {workout.hasResult || recorded.has(workout.id) ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-success/10 px-2 py-1 text-sm text-success"
                          data-testid={`today-result-recorded-${workout.id}`}
                        >
                          <IconCheck className="size-4" aria-hidden="true" />
                          Result recorded
                        </span>
                      ) : (
                        <Button onClick={() => setResults({ workout, mode: "record" })}>
                          Log a result
                        </Button>
                      )}
                      <Button variant="ghost" onClick={() => setResults({ workout, mode: "see" })}>
                        See results and comments
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}

      <Dialog open={results !== null} onOpenChange={(open) => !open && setResults(null)}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto"
          data-testid="workout-results-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {results?.mode === "record" ? "Record a result" : "Results and comments"}
            </DialogTitle>
            <DialogDescription>{results ? titleOf(results.workout) : ""}</DialogDescription>
          </DialogHeader>
          {results?.mode === "see" && (
            <ViewAthletesScoresWidget workoutCardUID={results.workout.id} />
          )}
          {results?.mode === "record" && (
            <WorkoutScore
              workoutUID={results.workout.id}
              onSubmitFinished={(saved?: boolean) => {
                if (saved !== true) return;
                const id = results.workout.id;
                setRecorded((prev) => new Set(prev).add(id));
                setResults(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
